import { useState, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import logo from "../components/assets/logo.png";
import Button from "./Button";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/about", label: "About Us" },
    { path: "/solar-calculator", label: "Solar Calculator" },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg"
            : "bg-white/90 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex justify-between items-center py-3 h-16 sm:h-20">
            {/* Logo - Smaller on mobile */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src={logo}
                  alt="Boost Solar"
                  className="h-16 sm:h-20 w-auto transition-transform duration-300 hover:scale-105"
                />
              </Link>
            </div>

            {/* Desktop Navigation - Hidden on small screens */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    location.pathname === item.path
                      ? "text-secondary"
                      : "text-gray-700 hover:text-secondary"
                  }`}
                >
                  {item.label}
                  {location.pathname === item.path && (
                    <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-secondary rounded-full"></span>
                  )}
                  <span className="absolute inset-0 bg-gradient-to-r from-secondary/10 to-opprimary/10 rounded-lg opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
                </Link>
              ))}
            </div>

            {/* Desktop Contact Button - Hidden on small screens */}
            <div className="hidden md:flex items-center">
              <Link to="/contact">
                <Button
                  variant="solid"
                  className="group bg-secondary hover:from-secondary/90 hover:to-opprimary/90 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 text-sm px-4 py-2.5"
                >
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    CONTACT US
                    {/* <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300 text-sm" /> */}
                  </span>
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button - Better spacing */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isScrolled
                    ? "bg-gray-100 hover:bg-gray-200"
                    : "bg-gray-50 hover:bg-gray-100"
                }`}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMenuOpen ? (
                  <FaTimes className="w-5 h-5 text-gray-700" />
                ) : (
                  <FaBars className="w-5 h-5 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden under navbar */}
      {/* <div className="h-16 sm:h-20" /> */}

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 z-50 transition-all duration-300 ease-in-out ${
          isMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isMenuOpen ? "opacity-40" : "opacity-0"
          }`}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Panel - Sliding from right, but smaller width */}
        <div
          className={`absolute right-0 top-0 h-full w-full max-w-xs bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <img src={logo} alt="Boost Solar" className="h-10 w-auto" />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                aria-label="Close menu"
              >
                <FaTimes className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Mobile Menu Items - Compact spacing */}
            <div className="flex-1 overflow-y-auto py-4">
              <div className="space-y-0.5 px-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center px-3 py-3 text-sm font-medium transition-all duration-200 ${
                      location.pathname === item.path
                        ? "bg-gradient-to-r from-secondary/10 to-opprimary/10 text-secondary border-l-3 border-secondary"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {item.label}
                    {location.pathname === item.path && (
                      <span className="ml-auto w-1.5 h-1.5 bg-secondary rounded-full"></span>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile Contact Button - Compact */}
            <div className="p-4 border-t border-gray-100">
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                <Button
                  variant="solid"
                  className="w-full bg-secondary  hover:from-secondary/90 hover:to-opprimary/90 shadow-md hover:shadow-lg transition-all duration-300 text-sm py-2.5"
                >
                  <span className="flex items-center justify-center gap-1.5">
                    CONTACT US
                    {/* <FaArrowRight className="text-xs" /> */}
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;